// ── Tenant brand colour resolver ─────────────────────────────────────
//
// The riverr design system reserves three CSS custom properties
// for the per-tenant brand colour:
//
//   --brand        primary CTA fill, scanner "ITEM ID" label,
//                  notification dot, scanner-armed focus border,
//                  welcome-card subtitle, checkmarks
//   --brand-dark   hover state for any element using --brand
//   --brand-soft   10% tint used for progress track bg and the
//                  150ms scan-confirmation flash
//
// All three derive from a single tenant `primaryColor` (hex). This
// helper:
//
//   1. Validates the input is a parseable hex colour
//   2. Validates contrast against --bg-card (#FFFFFF) is >= 4.5:1
//      (per the directive's accessibility rule for white-text-on-
//      brand CTAs)
//   3. If contrast fails, returns an auto-darkened variant that
//      DOES clear the threshold rather than failing closed —
//      keeps the app shippable for tenants with light brand
//      colours (e.g. yellow, lime) without forcing a content
//      review.
//   4. Computes `dark` (12% darken) and `soft` (10% rgba) variants
//
// Output is consumed by `VerticalLayout/index.js`, which pushes the
// three tokens onto `document.documentElement.style.*`.
//
// This file is intentionally framework-free — pure functions, no
// React, no Redux. Easy to unit-test, no DOM access.

const FALLBACK_BRAND = "#0f9a8a";
const ON_BRAND_LIGHT = "#ffffff";
const ON_BRAND_INK = "#14110e";
const CARD_BG_LUMINANCE = 1; // #FFFFFF
// Matches `:root[data-theme="dark"] { --bg-card }` in _design-tokens.scss.
const DARK_CARD_RGB = { r: 32, g: 31, b: 28 }; // #201f1c
const MIN_BRAND_ON_DARK_SURFACE = 3.0; // filled tile / border vs dark card
const MIN_WHITE_ON_BRAND = 4.5; // white CTA label on brand fill

const clamp = (n, lo = 0, hi = 255) => Math.max(lo, Math.min(hi, n));

const parseHex = (input) => {
  if (typeof input !== "string") return null;
  let h = input.trim().replace(/^#/, "");
  if (h.length === 3) {
    h = h
      .split("")
      .map((c) => c + c)
      .join("");
  }
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
};

const toHex = ({ r, g, b }) => {
  const h = (n) => clamp(Math.round(n)).toString(16).padStart(2, "0");
  return `#${h(r)}${h(g)}${h(b)}`;
};

// sRGB relative luminance per WCAG 2.1.
const luminance = ({ r, g, b }) => {
  const norm = (v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * norm(r) + 0.7152 * norm(g) + 0.0722 * norm(b);
};

const contrastRatio = (rgb) => {
  const L = luminance(rgb);
  // CARD_BG_LUMINANCE is 1 (pure white) — formula is
  // (max + 0.05) / (min + 0.05).
  return (CARD_BG_LUMINANCE + 0.05) / (L + 0.05);
};

const contrastBetween = (a, b) => {
  const La = luminance(a);
  const Lb = luminance(b);
  return (Math.max(La, Lb) + 0.05) / (Math.min(La, Lb) + 0.05);
};

const darken = ({ r, g, b }, amount) => ({
  r: clamp(r * (1 - amount)),
  g: clamp(g * (1 - amount)),
  b: clamp(b * (1 - amount)),
});

const rgbaString = ({ r, g, b }, alpha) =>
  `rgba(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}, ${alpha})`;

const mixWithWhite = ({ r, g, b }, whiteFraction) => ({
  r: clamp(r + (255 - r) * whiteFraction),
  g: clamp(g + (255 - g) * whiteFraction),
  b: clamp(b + (255 - b) * whiteFraction),
});

/** Indigo → teal gradient used on app.riverr.app only. */
export const RIVER_LOADER_COLORS = {
  from: "#4F5BF0",
  mid: "#6C7CFF",
  to: "#2FD6C4",
};

/**
 * Label colour for a filled brand surface. Picks white or ink based on
 * which has higher WCAG contrast against the background — so a dark red
 * CTA gets white type and a pale yellow CTA gets dark type.
 *
 * @param {string|undefined} hex
 * @returns {string}
 */
export const pickOnBrandText = (hex) => {
  const rgb = parseHex(hex);
  if (!rgb) return ON_BRAND_LIGHT;
  const white = { r: 255, g: 255, b: 255 };
  const ink = { r: 20, g: 17, b: 14 };
  return contrastBetween(rgb, white) >= contrastBetween(rgb, ink)
    ? ON_BRAND_LIGHT
    : ON_BRAND_INK;
};

/**
 * Resolves a tenant brand colour into the three CSS-variable values.
 *
 * @param {string|undefined} primaryColor - hex string from the tenant theme
 * @returns {{ brand: string, brandDark: string, brandSoft: string, onBrand: string, onBrandDark: string }}
 */
export const resolveTenantBrand = (primaryColor) => {
  let rgb = parseHex(primaryColor) || parseHex(FALLBACK_BRAND);

  // Auto-darken loop: if contrast against white is below 4.5:1,
  // darken by 8% steps until it passes (max 6 iterations to avoid
  // an infinite loop on degenerate inputs). 6 * 8% lands at ~38%
  // darken which is enough to push even pure yellow above the
  // threshold; if it still fails after that, we fall back to the
  // riverr-house teal so the chrome stays usable.
  let safety = 0;
  while (contrastRatio(rgb) < 4.5 && safety < 6) {
    rgb = darken(rgb, 0.08);
    safety += 1;
  }
  if (contrastRatio(rgb) < 4.5) {
    rgb = parseHex(FALLBACK_BRAND);
  }

  const brand = toHex(rgb);
  const brandDark = toHex(darken(rgb, 0.12));
  const brandSoft = rgbaString(rgb, 0.1);

  return {
    brand,
    brandDark,
    brandSoft,
    onBrand: pickOnBrandText(brand),
    onBrandDark: pickOnBrandText(brandDark),
  };
};

/**
 * Brand tokens tuned for dark surfaces (`--bg-card` in dark mode).
 *
 * `resolveTenantBrand` only validates white-label contrast on the light
 * card (#FFFFFF). A tenant whose brand is black/near-black passes that
 * check but disappears on dark POS tiles and CTAs. Lighten in small
 * steps until the fill reads against the dark card while keeping white
 * CTA text above AA.
 *
 * @param {string|undefined} primaryColor
 * @returns {{ brandOnDark: string, brandOnDarkDark: string, brandOnDarkSoft: string }}
 */
export const resolveBrandOnDark = (primaryColor) => {
  const { brand } = resolveTenantBrand(primaryColor);
  let rgb = parseHex(brand);

  let safety = 0;
  while (
    contrastBetween(rgb, DARK_CARD_RGB) < MIN_BRAND_ON_DARK_SURFACE &&
    safety < 14
  ) {
    rgb = mixWithWhite(rgb, 0.1);
    safety += 1;
  }

  // Don't over-lighten to the point white CTA labels fail (rare edge case).
  while (contrastRatio(rgb) < MIN_WHITE_ON_BRAND && safety > 0) {
    rgb = darken(rgb, 0.06);
    safety -= 1;
  }

  return {
    brandOnDark: toHex(rgb),
    brandOnDarkDark: toHex(darken(rgb, 0.12)),
    brandOnDarkSoft: rgbaString(rgb, 0.18),
  };
};

/**
 * Pushes the resolved brand tokens onto the document root. Idempotent.
 *
 * @param {string|undefined} primaryColor
 */
export const applyTenantBrand = (primaryColor) => {
  if (typeof document === "undefined") return;
  const { brand, brandDark, brandSoft, onBrand, onBrandDark } =
    resolveTenantBrand(primaryColor);
  const { brandOnDark, brandOnDarkDark, brandOnDarkSoft } =
    resolveBrandOnDark(primaryColor);
  document.documentElement.style.setProperty("--brand", brand);
  document.documentElement.style.setProperty("--brand-dark", brandDark);
  document.documentElement.style.setProperty("--brand-soft", brandSoft);
  document.documentElement.style.setProperty("--on-brand", onBrand);
  document.documentElement.style.setProperty("--on-brand-dark", onBrandDark);
  document.documentElement.style.setProperty("--brand-on-dark", brandOnDark);
  document.documentElement.style.setProperty(
    "--brand-on-dark-dark",
    brandOnDarkDark
  );
  document.documentElement.style.setProperty(
    "--brand-on-dark-soft",
    brandOnDarkSoft
  );
  // Customizer / design-studio accents read --rv-primary. Keep them in sync
  // with the tenant brand so nothing falls back to a hardcoded blue.
  document.documentElement.style.setProperty("--rv-primary", brand);
  document.documentElement.style.setProperty("--rv-primary-dark", brandDark);
  document.documentElement.style.setProperty("--rv-primary-soft", brandSoft);
};

/**
 * Sets the river-loader gradient on `:root`. Enterprise tenants stay neutral
 * until `primaryColor` arrives; app.riverr.app uses the house indigo→teal
 * gradient immediately (domain is known at boot).
 *
 * @param {string|undefined|null} primaryColor
 * @param {{ isRiverrDomain?: boolean }} [options]
 */
export const applyLoaderBrand = (primaryColor, { isRiverrDomain = false } = {}) => {
  if (typeof document === "undefined") return;

  let from;
  let mid;
  let to;

  if (isRiverrDomain) {
    ({ from, mid, to } = RIVER_LOADER_COLORS);
  } else {
    if (!parseHex(primaryColor)) return;
    const { brand, brandDark } = resolveTenantBrand(primaryColor);
    const brandRgb = parseHex(brand);
    from = brandDark;
    mid = brand;
    to = toHex(mixWithWhite(brandRgb, 0.45));
  }

  document.documentElement.style.setProperty("--loader-from", from);
  document.documentElement.style.setProperty("--loader-mid", mid);
  document.documentElement.style.setProperty("--loader-to", to);
};
